/**
 * AP-28 9.4 — der Import-Assistent `/articles/import`.
 *
 * Drei Schritte: **Datei** → **Zuordnung & Prüfung** (Trockenlauf, der Server
 * schreibt nichts) → **Ergebnis**. Die Übernahme ist erst klickbar, wenn der
 * Trockenlauf für genau diese Einstellungen gelaufen ist — sonst übernähme sie
 * etwas anderes, als die Tabelle zeigt.
 */

import { useCallback, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { erfuelltRecht } from '@symphenia/shared-types'
import { useAuthStore } from '@/lib/auth.store'
import { fehlertext } from '@/lib/fehlertext'
import { downloadFile } from '@/lib/download'
import { pageWide } from '@/lib/form-styles'
import {
  IMPORT_TARGETS,
  PURCHASE_TARGETS,
  articlesApi,
  type ImportOptions,
  type ImportPreview,
  type ImportResult,
  type ImportRow,
} from '../api/articles.api'

const eingabe =
  'w-full rounded-md border border-input bg-background px-2 py-1.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring'

const PUNKT: Record<ImportRow['action'], string> = {
  create: 'bg-primary',
  update: 'bg-green-600',
  skip: 'bg-muted-foreground',
  error: 'bg-destructive',
}

/** Wie viele Zeilen die Prüftabelle zeigt — Fehler kommen zuerst. */
const ANZEIGE_GRENZE = 300

export default function ArticleImportPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const darfEinkauf = erfuelltRecht('articles.purchase.view', user?.permissions)

  const [schritt, setSchritt] = useState<1 | 2 | 3>(1)
  const [datei, setDatei] = useState<File | null>(null)
  const [vorschau, setVorschau] = useState<ImportPreview | null>(null)
  const [optionen, setOptionen] = useState<ImportOptions>({ mode: 'upsert', createMissingLists: false })
  /** Für welche Einstellungen der letzte Trockenlauf galt. */
  const [geprueft, setGeprueft] = useState<string | null>(null)
  const [ergebnis, setErgebnis] = useState<ImportResult | null>(null)
  const [laeuft, setLaeuft] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)

  const ziele = IMPORT_TARGETS.filter((z) => darfEinkauf || !PURCHASE_TARGETS.includes(z))
  const signatur = JSON.stringify(optionen)

  const pruefen = useCallback(
    async (f: File, o: ImportOptions) => {
      setLaeuft(true)
      setFehler(null)
      try {
        const { data } = await articlesApi.importPreview(f, o)
        setVorschau(data)
        const naechste = { ...o, mapping: data.mapping }
        setOptionen(naechste)
        setGeprueft(JSON.stringify(naechste))
        setSchritt(2)
      } catch (err) {
        setFehler(fehlertext(err, t))
      } finally {
        setLaeuft(false)
      }
    },
    [t],
  )

  const uebernehmen = async () => {
    if (!datei) return
    setLaeuft(true)
    setFehler(null)
    try {
      const { data } = await articlesApi.importCommit(datei, optionen)
      setErgebnis(data)
      setSchritt(3)
    } catch (err) {
      setFehler(fehlertext(err, t))
    } finally {
      setLaeuft(false)
    }
  }

  const meldung = (m: ImportRow['messages'][number]) =>
    t(`articles.import.messages.${m.code}`, {
      defaultValue: m.code,
      field: m.feld ? t(`articles.import.targets.${m.feld}`) : '',
      value: m.wert ?? '',
    })

  const fehlerzeilenHerunterladen = (zeilen: ImportRow[]) => {
    const kopf = [t('articles.import.row'), t('articles.fields.articleNumber'), t('articles.fields.name'), t('articles.import.reason')]
    const inhalt = zeilen
      .filter((z) => z.action === 'error')
      .map((z) => [String(z.row), z.articleNumber ?? '', z.name ?? '', z.messages.filter((m) => m.stufe === 'error').map(meldung).join(' · ')])
    const csv = [kopf, ...inhalt].map((r) => r.map((v) => `"${v.replace(/"/g, '""')}"`).join(';')).join('\r\n')
    const bytes = new TextEncoder().encode('﻿' + csv)
    downloadFile(bytes.buffer as ArrayBuffer, 'artikel-import-fehler.csv', 'text/csv;charset=utf-8')
  }

  const sortiert = useMemo(() => {
    const zeilen = vorschau?.rows ?? []
    return [...zeilen.filter((z) => z.action === 'error'), ...zeilen.filter((z) => z.action !== 'error')].slice(0, ANZEIGE_GRENZE)
  }, [vorschau])

  return (
    <div className={pageWide}>
      <div>
        <Link to="/articles" className="text-sm text-muted-foreground hover:text-foreground">
          ‹ {t('articles.title')}
        </Link>
        <h1 className="mt-2 text-3xl font-bold text-foreground">{t('articles.import.title')}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{t('articles.import.subtitle')}</p>
      </div>

      <ol className="flex flex-wrap gap-2 text-sm">
        {[1, 2, 3].map((n) => (
          <li
            key={n}
            className={`flex items-center gap-2 rounded-full border px-3 py-1 ${
              schritt === n ? 'border-primary text-foreground' : 'border-border text-muted-foreground'
            }`}
          >
            <span
              className={`grid h-5 w-5 place-items-center rounded-full text-xs ${
                schritt === n ? 'bg-primary text-primary-foreground' : 'bg-muted'
              }`}
            >
              {n}
            </span>
            {t(`articles.import.steps.${n}`)}
          </li>
        ))}
      </ol>

      {fehler && <div className="rounded-md bg-destructive/10 px-4 py-3 text-sm text-destructive">{fehler}</div>}

      {/* ── 1 Datei ── */}
      {schritt === 1 && (
        <section className="rounded-lg border border-border bg-card p-6 shadow-sm">
          <p className="mb-4 text-sm text-muted-foreground">{t('articles.import.fileHint')}</p>
          <input
            type="file"
            accept=".xlsx,.csv,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            onChange={(e) => setDatei(e.target.files?.[0] ?? null)}
            className="block text-sm"
          />
          <div className="mt-4 flex justify-end">
            <button
              type="button"
              disabled={!datei || laeuft}
              onClick={() => datei && void pruefen(datei, { mode: 'upsert', createMissingLists: false })}
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {laeuft ? t('common.loading') : t('articles.import.next')}
            </button>
          </div>
        </section>
      )}

      {/* ── 2 Zuordnung & Prüfung ── */}
      {schritt === 2 && vorschau && datei && (
        <section className="space-y-4 rounded-lg border border-border bg-card p-6 shadow-sm">
          <p className="text-sm text-muted-foreground">
            {datei.name} · {t('articles.import.rowCount', { count: vorschau.totalRows })}
          </p>

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {ziele.map((ziel) => (
              <label key={ziel} className="block text-sm">
                <span className="mb-1 block text-muted-foreground">{t(`articles.import.targets.${ziel}`)}</span>
                <select
                  value={optionen.mapping?.[ziel] ?? ''}
                  onChange={(e) =>
                    setOptionen((o) => ({ ...o, mapping: { ...o.mapping, [ziel]: e.target.value || undefined } }))
                  }
                  className={eingabe}
                >
                  <option value="">—</option>
                  {vorschau.headers.map((h) => (
                    <option key={h} value={h}>
                      {h}
                    </option>
                  ))}
                </select>
              </label>
            ))}
          </div>

          <div className="flex flex-wrap items-center gap-6 text-sm">
            <label className="flex items-center gap-2">
              <span className="text-muted-foreground">{t('articles.import.mode')}</span>
              <select
                value={optionen.mode}
                onChange={(e) => setOptionen((o) => ({ ...o, mode: e.target.value as ImportOptions['mode'] }))}
                className={eingabe}
              >
                <option value="upsert">{t('articles.import.modeUpsert')}</option>
                <option value="create">{t('articles.import.modeCreate')}</option>
              </select>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={optionen.createMissingLists}
                onChange={(e) => setOptionen((o) => ({ ...o, createMissingLists: e.target.checked }))}
              />
              {t('articles.import.createMissingLists')}
            </label>
            <button
              type="button"
              disabled={laeuft}
              onClick={() => void pruefen(datei, optionen)}
              className="rounded-md border border-input px-3 py-1.5 text-sm text-foreground hover:bg-muted/50 disabled:opacity-50"
            >
              {laeuft ? t('common.loading') : t('articles.import.check')}
            </button>
          </div>

          <div className="flex flex-wrap gap-4 text-sm">
            {(['create', 'update', 'skip', 'error'] as const).map((a) => (
              <span key={a} className="flex items-center gap-1.5">
                <span className={`inline-block h-2 w-2 rounded-full ${PUNKT[a]}`} />
                {t(`articles.import.counts.${a}`, { count: vorschau.counts[a] })}
              </span>
            ))}
            {vorschau.unitsToCreate.length > 0 && (
              <span className="text-muted-foreground">{t('articles.import.unitsToCreate', { list: vorschau.unitsToCreate.join(', ') })}</span>
            )}
            {vorschau.groupsToCreate.length > 0 && (
              <span className="text-muted-foreground">{t('articles.import.groupsToCreate', { list: vorschau.groupsToCreate.join(', ') })}</span>
            )}
          </div>

          <div className="max-h-[28rem] overflow-auto rounded-md border border-border">
            <table className="w-full text-sm">
              <thead className="sticky top-0 border-b border-border bg-muted/80 text-left">
                <tr>
                  <th className="px-3 py-1.5 font-medium">{t('articles.import.row')}</th>
                  <th className="px-3 py-1.5 font-medium">{t('articles.fields.articleNumber')}</th>
                  <th className="px-3 py-1.5 font-medium">{t('articles.fields.name')}</th>
                  <th className="px-3 py-1.5 font-medium">{t('articles.import.result')}</th>
                </tr>
              </thead>
              <tbody>
                {sortiert.map((z) => (
                  <tr key={z.row} className="border-b border-border last:border-0">
                    <td className="px-3 py-1 tabular-nums text-muted-foreground">{z.row}</td>
                    <td className="px-3 py-1 font-mono">{z.articleNumber ?? '—'}</td>
                    <td className="px-3 py-1">{z.name ?? '—'}</td>
                    <td className="px-3 py-1">
                      <span className={`mr-2 inline-block h-2 w-2 rounded-full ${PUNKT[z.action]}`} />
                      {t(`articles.import.actions.${z.action}`)}
                      {z.messages.length > 0 && (
                        <span className={z.action === 'error' ? 'text-destructive' : 'text-muted-foreground'}>
                          {' · '}
                          {z.messages.map(meldung).join(' · ')}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {vorschau.rows.length > ANZEIGE_GRENZE && (
            <p className="text-xs text-muted-foreground">{t('articles.import.truncated', { count: ANZEIGE_GRENZE })}</p>
          )}

          <p className="rounded-md border-l-4 border-amber-500 bg-muted px-3 py-2 text-sm">{t('articles.import.transactionHint')}</p>

          <div className="flex flex-wrap justify-end gap-2">
            <button
              type="button"
              onClick={() => {
                setSchritt(1)
                setVorschau(null)
                setGeprueft(null)
              }}
              className="rounded-md border border-input px-3 py-1.5 text-sm text-foreground hover:bg-muted/50"
            >
              {t('articles.import.back')}
            </button>
            {vorschau.counts.error > 0 && (
              <button
                type="button"
                onClick={() => fehlerzeilenHerunterladen(vorschau.rows)}
                className="rounded-md border border-input px-3 py-1.5 text-sm text-foreground hover:bg-muted/50"
              >
                {t('articles.import.downloadErrors')}
              </button>
            )}
            <button
              type="button"
              disabled={laeuft || geprueft !== signatur || vorschau.counts.create + vorschau.counts.update === 0}
              title={geprueft !== signatur ? t('articles.import.checkFirst') : undefined}
              onClick={() => void uebernehmen()}
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {t('articles.import.commit', { count: vorschau.counts.create + vorschau.counts.update })}
            </button>
          </div>
        </section>
      )}

      {/* ── 3 Ergebnis ── */}
      {schritt === 3 && ergebnis && (
        <section className="space-y-4 rounded-lg border border-border bg-card p-6 shadow-sm">
          <ul className="space-y-1 text-sm">
            <li>{t('articles.import.done.created', { count: ergebnis.created })}</li>
            <li>{t('articles.import.done.updated', { count: ergebnis.updated })}</li>
            <li>{t('articles.import.done.skipped', { count: ergebnis.skipped })}</li>
            <li className={ergebnis.errors ? 'text-destructive' : ''}>{t('articles.import.done.errors', { count: ergebnis.errors })}</li>
            {ergebnis.unitsCreated + ergebnis.groupsCreated > 0 && (
              <li className="text-muted-foreground">
                {t('articles.import.done.lists', { units: ergebnis.unitsCreated, groups: ergebnis.groupsCreated })}
              </li>
            )}
          </ul>
          <div className="flex flex-wrap justify-end gap-2">
            {ergebnis.errors > 0 && (
              <button
                type="button"
                onClick={() => fehlerzeilenHerunterladen(ergebnis.rows)}
                className="rounded-md border border-input px-3 py-1.5 text-sm text-foreground hover:bg-muted/50"
              >
                {t('articles.import.downloadErrors')}
              </button>
            )}
            <button
              type="button"
              onClick={() => navigate('/articles')}
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
            >
              {t('articles.import.toList')}
            </button>
          </div>
        </section>
      )}
    </div>
  )
}
